import {
    CSSProperties,
    forwardRef,
    HTMLAttributes,
    ReactNode,
    Ref,
    UIEventHandler,
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import { getScrollOverflowEdges, ScrollOverflowEdges } from './overflowEdges';

export type OverflowFadeSurface = 'base' | 'container' | 'elevated';

export interface OverflowFadeScrollAreaProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'onScroll'> {
    children?: ReactNode;
    containerStyle?: CSSProperties;
    contentStyle?: CSSProperties;
    disabled?: boolean;
    fadeColor?: CSSProperties['color'];
    fadeSize?: CSSProperties['height'];
    fill?: boolean;
    onScroll?: UIEventHandler<HTMLDivElement>;
    surface?: OverflowFadeSurface;
}

const assignRef = <T,>(ref: Ref<T> | undefined, value: T | null) => {
    if (typeof ref === 'function') {
        ref(value);
    } else if (ref) {
        const mutableRef = ref as { current: T | null };
        mutableRef.current = value;
    }
};

/**
 * Adds non-interactive, token-aware edge fades to an independently scrolling
 * ancillary surface. Fades reflect the real scroll position and never mask a
 * region that has no overflow.
 */
const OverflowFadeScrollArea = forwardRef<HTMLDivElement, OverflowFadeScrollAreaProps>(
    (
        {
            children,
            containerStyle,
            contentStyle,
            disabled = false,
            fadeColor,
            fadeSize = '2.5rem',
            fill = false,
            onScroll,
            style,
            surface = 'container',
            ...scrollAreaProps
        },
        forwardedRef,
    ) => {
        const { token } = useAntDesignTheme();
        const scrollAreaRef = useRef<HTMLDivElement | null>(null);
        const contentRef = useRef<HTMLDivElement | null>(null);
        const animationFrameRef = useRef<number | undefined>(undefined);
        const [edges, setEdges] = useState<ScrollOverflowEdges>({ end: false, start: false });
        const setScrollAreaRef = useCallback(
            (node: HTMLDivElement | null) => {
                scrollAreaRef.current = node;
                assignRef(forwardedRef, node);
            },
            [forwardedRef],
        );
        const measure = useCallback(() => {
            const element = scrollAreaRef.current;

            if (!element || disabled) {
                setEdges((current) => (current.end || current.start ? { end: false, start: false } : current));
                return;
            }

            const nextEdges = getScrollOverflowEdges(element);
            setEdges((current) =>
                current.end === nextEdges.end && current.start === nextEdges.start ? current : nextEdges,
            );
        }, [disabled]);
        const scheduleMeasurement = useCallback(() => {
            if (typeof window === 'undefined') {
                return;
            }

            if (animationFrameRef.current !== undefined) {
                window.cancelAnimationFrame(animationFrameRef.current);
            }

            animationFrameRef.current = window.requestAnimationFrame(() => {
                animationFrameRef.current = undefined;
                measure();
            });
        }, [measure]);

        useLayoutEffect(() => {
            const scrollArea = scrollAreaRef.current;
            const content = contentRef.current;

            if (!scrollArea) {
                return;
            }

            scheduleMeasurement();

            const resizeObserver =
                typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(scheduleMeasurement);
            resizeObserver?.observe(scrollArea);
            if (content) {
                resizeObserver?.observe(content);
            }

            const mutationObserver =
                content && typeof MutationObserver !== 'undefined'
                    ? new MutationObserver(scheduleMeasurement)
                    : undefined;
            if (content) {
                mutationObserver?.observe(content, { characterData: true, childList: true, subtree: true });
            }

            window.addEventListener('resize', scheduleMeasurement);

            return () => {
                resizeObserver?.disconnect();
                mutationObserver?.disconnect();
                window.removeEventListener('resize', scheduleMeasurement);

                if (animationFrameRef.current !== undefined) {
                    window.cancelAnimationFrame(animationFrameRef.current);
                    animationFrameRef.current = undefined;
                }
            };
        }, [scheduleMeasurement]);

        const surfaceColor =
            fadeColor ??
            {
                base: token.colorBgBase,
                container: token.colorBgContainer,
                elevated: token.colorBgElevated,
            }[surface];
        const transparentSurface = `color-mix(in srgb, ${surfaceColor} 0%, transparent)`;
        const fadeTransition = `opacity ${token.motionDurationFast} ${token.motionEaseOut}`;
        const sharedFadeStyle: CSSProperties = {
            height: fadeSize,
            insetInline: 0,
            pointerEvents: 'none',
            position: 'absolute',
            transition: fadeTransition,
            zIndex: 2,
        };

        return (
            <div
                data-overflow-fade-root='true'
                data-overflow-surface={surface}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    minWidth: 0,
                    position: 'relative',
                    ...containerStyle,
                    overflow: 'hidden',
                }}
            >
                <div
                    {...scrollAreaProps}
                    data-overflow-at-end={edges.end ? 'true' : 'false'}
                    data-overflow-at-start={edges.start ? 'true' : 'false'}
                    data-overflow-fade-scroll-area='true'
                    onScroll={(event) => {
                        measure();
                        onScroll?.(event);
                    }}
                    ref={setScrollAreaRef}
                    style={{
                        boxSizing: 'border-box',
                        flex: fill ? '1 1 0%' : '1 1 auto',
                        maxHeight: fill ? '100%' : undefined,
                        minHeight: 0,
                        minWidth: 0,
                        overflowX: 'hidden',
                        overflowY: disabled ? 'hidden' : 'auto',
                        overscrollBehavior: 'contain',
                        scrollbarGutter: 'stable',
                        ...style,
                    }}
                >
                    <div ref={contentRef} style={{ minWidth: 0, ...contentStyle }}>
                        {children}
                    </div>
                </div>
                <div
                    aria-hidden='true'
                    data-overflow-fade='start'
                    style={{
                        ...sharedFadeStyle,
                        background: `linear-gradient(180deg, ${surfaceColor} 0%, ${transparentSurface} 100%)`,
                        opacity: edges.start ? 1 : 0,
                        top: 0,
                    }}
                />
                <div
                    aria-hidden='true'
                    data-overflow-fade='end'
                    style={{
                        ...sharedFadeStyle,
                        background: `linear-gradient(180deg, ${transparentSurface} 0%, ${surfaceColor} 100%)`,
                        bottom: 0,
                        opacity: edges.end ? 1 : 0,
                    }}
                />
            </div>
        );
    },
);

OverflowFadeScrollArea.displayName = 'OverflowFadeScrollArea';

export default OverflowFadeScrollArea;
